import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, Edit2, Trash2, Eye, EyeOff, Copy, ExternalLink, Save, X, Users, Upload, ImageIcon, Send, Calendar, Clock, ChevronUp, ChevronDown,
  // Icon options (mirror DealPage.jsx ICON_MAP so the picker shows the real icon)
  Building2, MapPin, BarChart3, CreditCard, Gem, Target, TrendingUp, Award, Star, Briefcase, Globe, Layers, Landmark,
  Wallet, Heart, Zap, Shield, Compass, Home, Check, Lock, Info, DollarSign, Handshake,
  Waves, Wine, UtensilsCrossed, Coffee, Dumbbell, Flower2, Sparkles, Crown, Trophy, ConciergeBell, Sofa, PartyPopper, Cake, Music,
  Pill, Cross, Stethoscope,
  Bed, Bath, Car, Sun, TreePine, Mountain, Sailboat, Anchor, Tv, ShoppingBag,
  Wifi, Camera, KeyRound, Bot, Snowflake, BatteryCharging, Phone, Mail, BellRing,
  Plane, Palette, Lightbulb, Gamepad2, Baby, Dog, Search,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon name → Lucide component map (mirrors DealPage.jsx ICON_MAP for visual parity)
const ICON_RENDER_MAP = {
  building: Building2, 'map-pin': MapPin, 'bar-chart': BarChart3, 'credit-card': CreditCard,
  gem: Gem, target: Target, 'trending-up': TrendingUp, users: Users, award: Award, star: Star,
  briefcase: Briefcase, globe: Globe, layers: Layers, landmark: Landmark, wallet: Wallet,
  heart: Heart, zap: Zap, shield: Shield, eye: Eye, compass: Compass, home: Home, check: Check,
  lock: Lock, info: Info, pound: DollarSign, dollar: DollarSign, handshake: Handshake,
  pool: Waves, bar: Wine, wine: Wine, restaurant: UtensilsCrossed, cafe: Coffee,
  gym: Dumbbell, spa: Flower2, sparkles: Sparkles, crown: Crown, trophy: Trophy,
  concierge: ConciergeBell, lounge: Sofa, events: PartyPopper, cake: Cake, music: Music,
  pharmacy: Pill, medical: Cross, hospital: Plus, doctor: Stethoscope,
  bed: Bed, bath: Bath, parking: Car, sun: Sun, garden: TreePine, mountain: Mountain,
  marina: Sailboat, anchor: Anchor, tv: Tv, shopping: ShoppingBag,
  wifi: Wifi, security: Camera, keyless: KeyRound, 'smart-home': Bot, climate: Snowflake,
  'ev-charging': BatteryCharging, clock: Clock, phone: Phone, mail: Mail, notification: BellRing,
  airport: Plane, design: Palette, innovation: Lightbulb, gaming: Gamepad2, kids: Baby, pet: Dog,
};

// Icon options grouped by category (used by IconPickerGrid + BulletIconPickerGrid)
const ICON_GROUPS = [
  { label: 'Core', icons: ['building', 'map-pin', 'bar-chart', 'credit-card', 'gem', 'target',
    'trending-up', 'users', 'award', 'star', 'briefcase', 'globe', 'layers', 'landmark',
    'wallet', 'heart', 'zap', 'shield', 'eye', 'compass', 'home', 'check', 'lock', 'info',
    'pound', 'handshake', 'dollar'] },
  { label: 'Luxury & Lifestyle', icons: ['pool', 'bar', 'restaurant', 'cafe', 'gym', 'spa',
    'sparkles', 'crown', 'trophy', 'concierge', 'lounge', 'events', 'cake', 'wine', 'music'] },
  { label: 'Healthcare', icons: ['pharmacy', 'medical', 'hospital', 'doctor'] },
  { label: 'Property', icons: ['bed', 'bath', 'parking', 'sun', 'garden', 'mountain', 'marina',
    'anchor', 'tv', 'shopping'] },
  { label: 'Smart & Security', icons: ['wifi', 'security', 'keyless', 'smart-home', 'climate',
    'ev-charging', 'clock', 'phone', 'mail', 'notification'] },
  { label: 'Other', icons: ['airport', 'design', 'innovation', 'gaming', 'kids', 'pet'] },
];
const ICON_OPTIONS = ICON_GROUPS.flatMap(g => g.icons);

const SAMPLE_DETAIL_CARDS = [
  { icon: 'building', title: 'PROPERTY OVERVIEW', items: ['Premium mixed-use residential development', 'Luxury Studios', '1, 2 & 3 Bedroom Apartments', 'Premium Penthouse Collection', 'Curated lifestyle-driven architecture'] },
  { icon: 'map-pin', title: 'LOCATION ADVANTAGE', items: ["Prime location – one of the strongest residential and investment districts with:", 'High-income residential demand', 'Expatriate & executive market appeal', 'Strong commercial & lifestyle ecosystem', 'Strong long-term appreciation profile'] },
  { icon: 'bar-chart', title: 'INVESTMENT SNAPSHOT', items: ['Studios from US$63,900', '1 Beds from US$138,663', '2 Beds from US$240,903', '3 Beds from US$304,590+', 'Penthouses up to US$978,415+'] },
  { icon: 'credit-card', title: 'PAYMENT STRUCTURE', highlight: '20% DOWN PAYMENT | BALANCE SPREAD OVER UP TO 4 YEARS', items: ['US$5,000 reservation secures pricing (21 days)', '10% cash purchase discount', 'Installment plans available', 'Mortgage option available', 'Structured staged payments to handover'] },
  { icon: 'gem', title: 'LIFESTYLE & AMENITIES', items: ['Rooftop Pool', 'Cloud Bar & Restaurant', 'Digital Fitness Lounge', 'Beauty Spa & Sauna', '24/7 Security & CCTV', 'EV Charging Infrastructure'] },
  { icon: 'target', title: 'INVESTOR HIGHLIGHTS', items: ['Off-plan pricing advantage', 'Strong capital appreciation potential', 'Luxury rental / diaspora buyer appeal', 'Lifestyle + investment positioning', 'Multiple exit strategies'] },
];

const SAMPLE_PILLARS = [
  { icon: 'shield', title: 'OFF-PLAN PRICING ADVANTAGE' },
  { icon: 'trending-up', title: 'HIGH-GROWTH LOCATION' },
  { icon: 'gem', title: 'PREMIUM LIFESTYLE ECOSYSTEM' },
  { icon: 'briefcase', title: 'STRONG INVESTMENT FUNDAMENTALS' },
];

// 6 small badges shown beside the key facts (3x2 grid). Optional — leave empty to hide the column.
const SAMPLE_FEATURE_BADGES = [
  { icon: 'gem', title: 'Premium Residences' },
  { icon: 'trending-up', title: 'Strong Growth Location' },
  { icon: 'shield', title: 'Secure & Flexible Ownership' },
  { icon: 'star', title: 'World-Class Lifestyle' },
  { icon: 'users', title: 'Executive & Diaspora Appeal' },
  { icon: 'wallet', title: 'High Capital Appreciation' },
];

// 5-cell Bloomberg-style returns strip shown below the CTA panel.
const SAMPLE_RETURNS_STRIP = [
  { label: 'Projected Yield', value: '8–22%', caption: 'gross' },
  { label: 'Capital Growth', value: '8–15% CAGR', caption: '' },
  { label: 'Target Rental', value: 'USD 800–4,000+ pcm', caption: '' },
  { label: 'Investor Strategies', value: 'BTL · Airbnb · Diaspora · Growth', caption: '' },
  { label: 'Entry Position', value: '20% Deposit', caption: '' },
];
const SAMPLE_RETURNS_DISCLAIMER = 'Actual performance subject to location, unit mix, market conditions, operating strategy, management efficiency, and completion timing.';

export const DealsTab = ({ getStoredPassword }) => {
  const [deals, setDeals] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showEnquiries, setShowEnquiries] = useState(false);
  const [filterUnnotified, setFilterUnnotified] = useState(false);
  const passwordRef = useRef(null);
  const fileInputRef = useRef(null);

  function defaultForm() {
    return {
      title: '', deal_id: '', strategy_tags: ['LUXURY RESIDENTIAL'],
      finders_fee: 5000, finders_fee_currency: 'USD',
      location: '', property_type: '',
      positioning: 'Off-Plan | High Growth | Lifestyle & Investment Opportunity',
      other_info: '', hero_image: '',
      urgency_text: 'Deals available on a first-come, first-served basis.',
      urgency_subtext: 'Secure positioning during development phase before potential completion-driven value uplift.',
      cta_button_text: 'Access This Opportunity',
      headline_title: 'HEADLINE DEAL DETAILS',
      headline_subtitle: '',
      detail_cards: SAMPLE_DETAIL_CARDS,
      pillars: SAMPLE_PILLARS,
      feature_badges: SAMPLE_FEATURE_BADGES,
      gallery: [],
      returns_strip: SAMPLE_RETURNS_STRIP,
      returns_disclaimer: SAMPLE_RETURNS_DISCLAIMER,
      summary: 'This is more than a property acquisition – it is a premium urban lifestyle and future-focused investment platform.',
      disclosure_text: 'Serious interest / full project disclosure available upon engagement.',
      details: [],
      visible: true, featured: false,
    };
  }

  useEffect(() => {
    passwordRef.current = getStoredPassword();
    const fetch_ = async () => {
      const pw = passwordRef.current;
      try {
        const [dRes, eRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/deals?password=${pw}`),
          fetch(`${API_URL}/api/admin/deal-enquiries?password=${pw}`)
        ]);
        if (dRes.ok) setDeals(await dRes.json());
        if (eRes.ok) setEnquiries(await eRes.json());
      } catch {}
      setLoading(false);
    };
    fetch_();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pw = () => passwordRef.current || localStorage.getItem('adminPassword');

  const saveDeal = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    const isNew = editing === 'new';
    const url = isNew
      ? `${API_URL}/api/admin/deals?password=${pw()}`
      : `${API_URL}/api/admin/deals/${editing}?password=${pw()}`;
    try {
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(isNew ? 'Deal created' : 'Deal updated');
        setEditing(null);
        const dRes = await fetch(`${API_URL}/api/admin/deals?password=${pw()}`);
        if (dRes.ok) setDeals(await dRes.json());
      } else {
        // Surface the real backend error so admins know what to fix
        let detail = `HTTP ${res.status}`;
        try {
          const data = await res.json();
          if (Array.isArray(data?.detail)) {
            detail = data.detail.map(d => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join('; ');
          } else if (typeof data?.detail === 'string') {
            detail = data.detail;
          }
        } catch { /* ignore */ }
        toast.error(`Save failed — ${detail}`, { duration: 8000 });
        console.error('[saveDeal] failed', { status: res.status, detail, form });
      }
    } catch (e) {
      toast.error(`Save failed — network: ${e?.message || 'unknown'}`, { duration: 8000 });
      console.error('[saveDeal] network', e);
    }
    setSaving(false);
  };

  const deleteDeal = async (id) => {
    if (!window.confirm('Delete this deal?')) return;
    try {
      await fetch(`${API_URL}/api/admin/deals/${id}?password=${pw()}`, { method: 'DELETE' });
      setDeals(deals.filter(d => d.id !== id));
      toast.success('Deal deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const toggleVisibility = async (deal) => {
    try {
      await fetch(`${API_URL}/api/admin/deals/${deal.id}?password=${pw()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...deal, visible: !deal.visible })
      });
      setDeals(deals.map(d => d.id === deal.id ? { ...d, visible: !d.visible } : d));
    } catch { toast.error('Failed to toggle'); }
  };

  const copyLink = (deal) => {
    const url = `${window.location.origin}/deals/${deal.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Shareable link copied!');
  };

  const notifySubscribers = async (deal) => {
    if (!deal.visible) { toast.error('Make the deal Live first, then notify.'); return; }
    if (!window.confirm(`Email the mailing list about "${(deal.title||'').slice(0,60)}"?`)) return;
    const t = toast.loading('Sending to mailing list…');
    try {
      const res = await fetch(`${API_URL}/api/admin/deals/${deal.id}/notify-subscribers?password=${pw()}`, { method: 'POST' });
      const data = await res.json();
      toast.dismiss(t);
      if (res.ok) {
        toast.success(`Sent to ${data.sent}/${data.total} subscribers${data.failed ? ` (${data.failed} failed)` : ''}`);
        // Refresh deal list to pick up last_notified_at
        const dRes = await fetch(`${API_URL}/api/admin/deals?password=${pw()}`);
        if (dRes.ok) setDeals(await dRes.json());
      } else {
        toast.error(data.detail || 'Failed to send');
      }
    } catch {
      toast.dismiss(t);
      toast.error('Network error');
    }
  };

  const startEdit = (deal) => {
    setEditing(deal.id);
    setForm({
      title: deal.title || '', deal_id: deal.deal_id || '',
      strategy_tags: deal.strategy_tags || [],
      finders_fee: deal.finders_fee || 0,
      finders_fee_currency: deal.finders_fee_currency || 'USD',
      location: deal.location || '', property_type: deal.property_type || '',
      positioning: deal.positioning || '',
      other_info: deal.other_info || '',
      hero_image: deal.hero_image || '',
      urgency_text: deal.urgency_text || '',
      urgency_subtext: deal.urgency_subtext || '',
      cta_button_text: deal.cta_button_text || 'Access This Opportunity',
      headline_title: deal.headline_title || 'HEADLINE DEAL DETAILS',
      headline_subtitle: deal.headline_subtitle || '',
      detail_cards: deal.detail_cards && deal.detail_cards.length ? deal.detail_cards : [],
      pillars: deal.pillars && deal.pillars.length ? deal.pillars : [],
      feature_badges: deal.feature_badges && deal.feature_badges.length ? deal.feature_badges : [],
      gallery: Array.isArray(deal.gallery) ? deal.gallery : [],
      returns_strip: Array.isArray(deal.returns_strip) ? deal.returns_strip : [],
      returns_disclaimer: deal.returns_disclaimer || '',
      summary: deal.summary || '',
      disclosure_text: deal.disclosure_text || 'Serious interest / full project disclosure available upon engagement.',
      details: deal.details || [],
      visible: deal.visible !== false, featured: deal.featured || false,
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/upload/logo?password=${pw()}`, { method: 'POST', body: fd });
      if (res.ok) {
        const data = await res.json();
        setForm(f => ({ ...f, hero_image: data.image_url }));
        toast.success('Image uploaded');
      } else toast.error('Upload failed');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  // ============ Gallery handling ============
  const galleryInputRef = useRef(null);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setGalleryUploading(true);
    const uploaded = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/upload/logo?password=${pw()}`, { method: 'POST', body: fd });
        if (res.ok) {
          const data = await res.json();
          uploaded.push(data.image_url);
        }
      } catch { /* skip failed */ }
    }
    if (uploaded.length) {
      setForm(f => ({ ...f, gallery: [...(f.gallery || []), ...uploaded] }));
      toast.success(`${uploaded.length} image(s) added to gallery`);
    } else toast.error('Gallery upload failed');
    setGalleryUploading(false);
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };
  const removeGalleryImage = (i) => setForm(f => ({ ...f, gallery: (f.gallery || []).filter((_, j) => j !== i) }));
  const moveGalleryImage = (i, dir) => {
    const arr = [...(form.gallery || [])];
    const newIdx = i + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[i], arr[newIdx]] = [arr[newIdx], arr[i]];
    setForm({ ...form, gallery: arr });
  };

  // ============ Schedule notification ============
  const [scheduleModal, setScheduleModal] = useState(null); // { deal } or null
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [showSchedules, setShowSchedules] = useState(false);

  const refreshSchedules = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/scheduled-notifications?password=${pw()}`);
      if (res.ok) setSchedules(await res.json());
    } catch { /* ignore */ }
  };
  useEffect(() => { refreshSchedules(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const submitSchedule = async () => {
    if (!scheduleDate || !scheduleTime) { toast.error('Pick a date & time'); return; }
    const local = new Date(`${scheduleDate}T${scheduleTime}`);
    if (isNaN(local.getTime())) { toast.error('Invalid date/time'); return; }
    if (local.getTime() <= Date.now()) { toast.error('Scheduled time must be in the future'); return; }
    const isoUtc = local.toISOString();
    const t = toast.loading('Scheduling…');
    try {
      const res = await fetch(`${API_URL}/api/admin/deals/${scheduleModal.deal.id}/schedule-notify?password=${pw()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scheduled_at: isoUtc }),
      });
      toast.dismiss(t);
      if (res.ok) {
        toast.success(`Notification scheduled for ${local.toLocaleString()}`);
        setScheduleModal(null);
        setScheduleDate(''); setScheduleTime('');
        refreshSchedules();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to schedule');
      }
    } catch {
      toast.dismiss(t);
      toast.error('Network error');
    }
  };

  const cancelSchedule = async (id) => {
    if (!window.confirm('Cancel this scheduled notification?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/scheduled-notifications/${id}?password=${pw()}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Cancelled'); refreshSchedules(); }
      else toast.error('Failed to cancel');
    } catch { toast.error('Network error'); }
  };

  // ============ Tag handling ============
  const [tagInput, setTagInput] = useState('');
  const addTag = () => {
    if (!tagInput.trim()) return;
    setForm({ ...form, strategy_tags: [...form.strategy_tags, tagInput.trim().toUpperCase()] });
    setTagInput('');
  };

  // ============ Detail Card handling ============
  const addDetailCard = () => setForm({ ...form, detail_cards: [...form.detail_cards, { icon: 'info', title: 'NEW CARD', items: [] }] });
  const updateCard = (idx, field, value) => {
    const copy = [...form.detail_cards];
    copy[idx] = { ...copy[idx], [field]: value };
    setForm({ ...form, detail_cards: copy });
  };
  const removeCard = (idx) => setForm({ ...form, detail_cards: form.detail_cards.filter((_, i) => i !== idx) });

  // ============ Pillar handling ============
  const addPillar = () => {
    if (form.pillars.length >= 4) { toast.info('Max 4 pillars'); return; }
    setForm({ ...form, pillars: [...form.pillars, { icon: 'check', title: 'NEW PILLAR' }] });
  };
  const updatePillar = (idx, field, value) => {
    const copy = [...form.pillars];
    copy[idx] = { ...copy[idx], [field]: value };
    setForm({ ...form, pillars: copy });
  };
  const removePillar = (idx) => setForm({ ...form, pillars: form.pillars.filter((_, i) => i !== idx) });

  // ============ Feature Badge handling (small 3x2 grid beside key facts) ============
  const addBadge = () => {
    if (form.feature_badges.length >= 6) { toast.info('Max 6 badges'); return; }
    setForm({ ...form, feature_badges: [...form.feature_badges, { icon: 'gem', title: 'New Badge' }] });
  };
  const updateBadge = (idx, field, value) => {
    const copy = [...form.feature_badges];
    copy[idx] = { ...copy[idx], [field]: value };
    setForm({ ...form, feature_badges: copy });
  };
  const removeBadge = (idx) => setForm({ ...form, feature_badges: form.feature_badges.filter((_, i) => i !== idx) });

  if (loading) return <div className="text-center text-white/50 py-8">Loading...</div>;

  // Apply un-notified filter (only show Live deals that have never been emailed to mailing list)
  const visibleDeals = filterUnnotified
    ? deals.filter(d => d.visible !== false && !d.last_notified_at)
    : deals;
  const unnotifiedCount = deals.filter(d => d.visible !== false && !d.last_notified_at).length;

  // ============ EDIT / CREATE FORM ============
  if (editing !== null) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between sticky top-0 bg-[#0a1a14] py-3 z-10 -mx-1 px-1">
          <h3 className="text-lg text-white">{editing === 'new' ? 'Create New Deal' : 'Edit Deal'}</h3>
          <div className="flex items-center gap-2">
            <Button onClick={() => setEditing(null)} variant="ghost" size="sm" className="text-white/50" data-testid="cancel-deal-btn">Cancel</Button>
            <Button onClick={saveDeal} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" disabled={saving} data-testid="save-deal-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Deal'}
            </Button>
          </div>
        </div>

        {/* Hero / Core */}
        <Section title="Hero & Core Information">
          <Field label="Deal Title *">
            <Textarea
              rows={3}
              value={form.title}
              onChange={e => setForm({...form, title: e.target.value})}
              placeholder={"Press Enter for line breaks. Example:\nEXCLUSIVE ACCRA AIRPORT CITY |\nLUXURY DEVELOPMENT | PREMIUM RESIDENCES |\nFLEXIBLE OWNERSHIP | FROM USD 225,150"}
              className="bg-white/5 border-white/10 text-white whitespace-pre-line"
              data-testid="deal-title-input"
            />
            <p className="text-[10px] text-white/40 mt-1">Tip: Use Enter to control where each line breaks in the hero. Line 1 renders in gold; lines 2+ render in white. All <span className="text-[#D4AF37]">|</span> separators stay gold.</p>
          </Field>

          {/* Hero Image */}
          <Field label="Hero Image (right of title)">
            <div className="flex items-center gap-3">
              {form.hero_image ? (
                <div className="relative w-24 h-16 rounded-md overflow-hidden border border-white/10 flex-shrink-0">
                  <img src={form.hero_image} alt="hero" className="w-full h-full object-cover" />
                  <button onClick={() => setForm({...form, hero_image: ''})} className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 rounded text-white/70 hover:text-red-400"><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <div className="w-24 h-16 rounded-md border border-dashed border-white/15 flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="w-5 h-5 text-white/20" />
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              <Button onClick={() => fileInputRef.current?.click()} variant="outline" size="sm" className="border-white/10 text-white/70 h-8" disabled={uploading} data-testid="upload-hero-btn">
                <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploading ? 'Uploading...' : (form.hero_image ? 'Replace' : 'Upload Photo')}
              </Button>
              <Input value={form.hero_image} onChange={e => setForm({...form, hero_image: e.target.value})} placeholder="or paste image URL..." className="bg-white/5 border-white/10 text-white text-xs flex-1" />
            </div>
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Deal ID">
              <Input value={form.deal_id} onChange={e => setForm({...form, deal_id: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="e.g., GH1025" />
            </Field>
            <Field label="Founders Fee">
              <Input type="number" value={form.finders_fee} onChange={e => setForm({...form, finders_fee: parseFloat(e.target.value) || 0})} className="bg-white/5 border-white/10 text-white text-xs" />
            </Field>
            <Field label="Currency">
              <select value={form.finders_fee_currency} onChange={e => setForm({...form, finders_fee_currency: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white text-xs focus:outline-none focus:border-[#D4AF37]/40">
                <option value="USD" className="bg-[#0a1a14]">USD (US$)</option>
                <option value="GBP" className="bg-[#0a1a14]">GBP (£)</option>
                <option value="EUR" className="bg-[#0a1a14]">EUR (€)</option>
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Location">
              <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="e.g., East Legon, Accra, Ghana" />
            </Field>
            <Field label="Asset Type / Property Type">
              <Input value={form.property_type} onChange={e => setForm({...form, property_type: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="e.g., Luxury Residential Development" />
            </Field>
          </div>

          <Field label="Positioning">
            <Input value={form.positioning} onChange={e => setForm({...form, positioning: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="e.g., Off-Plan | High Growth | Lifestyle & Investment Opportunity" />
          </Field>

          {/* Strategy tags */}
          <Field label="Strategy Tags (shown as gold chips under the title)">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.strategy_tags.map((tag, i) => (
                <span key={i} className="flex items-center gap-1 px-2 py-0.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] text-[11px] font-medium rounded">
                  {tag}
                  <button onClick={() => setForm({...form, strategy_tags: form.strategy_tags.filter((_, j) => j !== i)})} className="hover:text-red-400"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} className="bg-white/5 border-white/10 text-white text-xs flex-1" placeholder="e.g., LUXURY RESIDENTIAL" />
              <Button onClick={addTag} variant="outline" size="sm" className="border-white/10 text-white/60 text-xs">Add</Button>
            </div>
          </Field>

          <div className="flex items-center gap-4 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.visible} onChange={e => setForm({...form, visible: e.target.checked})} className="accent-[#D4AF37]" />
              <span className="text-white/60 text-xs">Visible on site</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} className="accent-[#D4AF37]" />
              <span className="text-white/60 text-xs">Featured</span>
            </label>
          </div>
        </Section>

        {/* CTA & Urgency */}
        <Section title={`Gallery (${(form.gallery || []).length}) — optional additional photos`} action={
          <>
            <input ref={galleryInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleGalleryUpload} />
            <Button onClick={() => galleryInputRef.current?.click()} variant="outline" size="sm" className="border-[#D4AF37]/30 text-[#D4AF37] text-xs h-7" disabled={galleryUploading} data-testid="add-gallery-btn">
              <Upload className="w-3 h-3 mr-1" /> {galleryUploading ? 'Uploading…' : 'Add Photos'}
            </Button>
          </>
        }>
          {(form.gallery || []).length === 0 ? (
            <p className="text-white/30 text-xs italic">No gallery images. Add additional property photos to display in a thumbnail strip above the footer.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {(form.gallery || []).map((url, i) => (
                <div key={i} className="relative group rounded-md overflow-hidden border border-white/10" data-testid={`gallery-image-${i}`}>
                  <img src={url} alt={`gallery ${i}`} className="w-full h-24 object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    <button onClick={() => moveGalleryImage(i, -1)} disabled={i === 0} className="p-1.5 bg-white/10 hover:bg-white/20 rounded disabled:opacity-30" title="Move left"><ChevronUp className="w-3 h-3 text-white" /></button>
                    <button onClick={() => moveGalleryImage(i, 1)} disabled={i === form.gallery.length - 1} className="p-1.5 bg-white/10 hover:bg-white/20 rounded disabled:opacity-30" title="Move right"><ChevronDown className="w-3 h-3 text-white" /></button>
                    <button onClick={() => removeGalleryImage(i)} className="p-1.5 bg-red-500/30 hover:bg-red-500/60 rounded" title="Remove"><Trash2 className="w-3 h-3 text-white" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="CTA & Urgency">
          <Field label="CTA Button Text">
            <Input value={form.cta_button_text} onChange={e => setForm({...form, cta_button_text: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="Access This Opportunity" />
          </Field>
          <Field label="Urgency Headline">
            <Input value={form.urgency_text} onChange={e => setForm({...form, urgency_text: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" />
          </Field>
          <Field label="Urgency Subtext">
            <textarea value={form.urgency_subtext} onChange={e => setForm({...form, urgency_subtext: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white text-xs focus:outline-none focus:border-[#D4AF37]/40 min-h-[60px] resize-y" />
          </Field>
        </Section>

        {/* Returns Strip Editor (5-cell premium ledger below CTA) */}
        <Section title={`Returns Strip (${(form.returns_strip || []).length}) — premium 5-cell panel below the CTA`} action={
          (form.returns_strip || []).length < 5 ? (
            <Button onClick={() => setForm({ ...form, returns_strip: [...(form.returns_strip || []), { label: 'NEW', value: '', caption: '' }] })}
              variant="outline" size="sm" className="border-[#D4AF37]/30 text-[#D4AF37] text-xs h-7" data-testid="add-returns-cell-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Cell
            </Button>
          ) : null
        }>
          {(form.returns_strip || []).length === 0 ? (
            <p className="text-white/30 text-xs italic">Empty — the returns ledger panel will be hidden on the public deal page. Click "Add Cell" (max 5) to enable a Bloomberg-style returns summary below the CTA.</p>
          ) : (
            <div className="space-y-2">
              {form.returns_strip.map((cell, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[160px_1fr_140px_28px] gap-2 items-center bg-[#0d1f17] border border-white/10 rounded-md p-2.5" data-testid={`returns-cell-editor-${i}`}>
                  <Input value={cell.label} onChange={e => {
                    const arr=[...form.returns_strip]; arr[i]={...arr[i],label:e.target.value}; setForm({...form,returns_strip:arr});
                  }} className="bg-white/5 border-white/10 text-[#D4AF37] text-[11px] h-8 font-bold uppercase tracking-wider" placeholder="LABEL e.g. PROJECTED YIELD" />
                  <Input value={cell.value} onChange={e => {
                    const arr=[...form.returns_strip]; arr[i]={...arr[i],value:e.target.value}; setForm({...form,returns_strip:arr});
                  }} className="bg-white/5 border-white/10 text-white text-xs h-8 font-bold" placeholder="VALUE e.g. 8–22%" />
                  <Input value={cell.caption || ''} onChange={e => {
                    const arr=[...form.returns_strip]; arr[i]={...arr[i],caption:e.target.value}; setForm({...form,returns_strip:arr});
                  }} className="bg-white/5 border-white/10 text-white/60 text-[10px] h-8 italic" placeholder="caption (optional)" />
                  <button onClick={() => setForm({ ...form, returns_strip: form.returns_strip.filter((_, j) => j !== i) })}
                    className="p-1 hover:bg-red-500/20 rounded text-red-400 justify-self-center">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <Field label="Returns Disclaimer (italic, shown below the strip)">
            <textarea value={form.returns_disclaimer || ''} onChange={e => setForm({ ...form, returns_disclaimer: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white/80 text-xs focus:outline-none focus:border-[#D4AF37]/40 min-h-[60px] resize-y italic"
              placeholder="e.g., Actual performance subject to location, unit mix, market conditions, operating strategy, management efficiency, and completion timing." />
          </Field>
        </Section>

        {/* Headline Section */}
        <Section title="Headline Deal Details Section">
          <Field label="Section Title">
            <Input value={form.headline_title} onChange={e => setForm({...form, headline_title: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="HEADLINE DEAL DETAILS" />
          </Field>
          <Field label="Subtitle Banner (gold bar under title — optional)">
            <Input value={form.headline_subtitle} onChange={e => setForm({...form, headline_subtitle: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="e.g., Exclusive off-market opportunity" />
          </Field>
        </Section>

        {/* Detail Cards Editor */}
        <Section title={`Detail Cards (${form.detail_cards.length}) — shown in 2x3 grid`} action={
          <Button onClick={addDetailCard} variant="outline" size="sm" className="border-[#D4AF37]/30 text-[#D4AF37] text-xs h-7" data-testid="add-card-btn">
            <Plus className="w-3 h-3 mr-1" /> Add Card
          </Button>
        }>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {form.detail_cards.map((card, i) => (
              <CardEditor key={i} card={card} idx={i} onUpdate={updateCard} onRemove={removeCard} />
            ))}
            {form.detail_cards.length === 0 && (
              <p className="text-white/30 text-xs italic col-span-full">No detail cards yet. Click "Add Card" above.</p>
            )}
          </div>
        </Section>

        {/* Feature Badges Editor (3x2 grid beside key facts) */}
        <Section title={`Feature Badges (${form.feature_badges.length}/6) — small icons beside key facts`} action={
          form.feature_badges.length < 6 ? (
            <Button onClick={addBadge} variant="outline" size="sm" className="border-[#D4AF37]/30 text-[#D4AF37] text-xs h-7" data-testid="add-badge-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Badge
            </Button>
          ) : null
        }>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {form.feature_badges.map((b, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/[0.02] border border-white/5 rounded-md p-2">
                <IconPicker value={b.icon} onChange={v => updateBadge(i, 'icon', v)} />
                <Input value={b.title} onChange={e => updateBadge(i, 'title', e.target.value)} className="bg-white/5 border-white/10 text-white text-xs flex-1" placeholder="e.g. Premium Residences" />
                <button onClick={() => removeBadge(i)} className="p-1 hover:bg-red-500/20 rounded text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {form.feature_badges.length === 0 && (
              <p className="text-white/30 text-xs italic col-span-full">Empty — the key-facts panel will display full-width. Click "Add Badge" to enable the 3×2 right-column grid (max 6).</p>
            )}
          </div>
        </Section>

        {/* Pillars Editor */}
        <Section title={`Pillars Banner (${form.pillars.length}/4) — bottom highlight strip`} action={
          form.pillars.length < 4 ? (
            <Button onClick={addPillar} variant="outline" size="sm" className="border-[#D4AF37]/30 text-[#D4AF37] text-xs h-7" data-testid="add-pillar-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Pillar
            </Button>
          ) : null
        }>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {form.pillars.map((p, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/[0.02] border border-white/5 rounded-md p-2">
                <IconPicker value={p.icon} onChange={v => updatePillar(i, 'icon', v)} />
                <Input value={p.title} onChange={e => updatePillar(i, 'title', e.target.value)} className="bg-white/5 border-white/10 text-white text-xs flex-1" placeholder="Pillar title" />
                <button onClick={() => removePillar(i)} className="p-1 hover:bg-red-500/20 rounded text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </Section>

        {/* Footer */}
        <Section title="Footer Summary & Disclosure">
          <Field label="Summary Paragraph (left of footer)">
            <textarea value={form.summary} onChange={e => setForm({...form, summary: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white text-xs focus:outline-none focus:border-[#D4AF37]/40 min-h-[80px] resize-y" placeholder="This is more than a property acquisition – it is a premium urban lifestyle..." />
          </Field>
          <Field label="Disclosure Text (right of footer, with lock icon)">
            <Input value={form.disclosure_text} onChange={e => setForm({...form, disclosure_text: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="Serious interest / full project disclosure available upon engagement." />
          </Field>
        </Section>

        {/* Save bar (bottom) */}
        <div className="flex items-center justify-end gap-2 pt-2 pb-6">
          <Button onClick={() => setEditing(null)} variant="ghost" size="sm" className="text-white/50">Cancel</Button>
          <Button onClick={saveDeal} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" disabled={saving} data-testid="save-deal-btn-bottom">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Deal'}
          </Button>
        </div>
      </div>
    );
  }

  // ============ LIST VIEW ============
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg text-white">Deal Sourcing</h3>
          <p className="text-white/40 text-sm">Manage investment deals and enquiries</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setFilterUnnotified(!filterUnnotified)} variant="outline" size="sm"
            className={`h-9 border-white/10 ${filterUnnotified ? 'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30' : 'text-white/60 hover:text-white'}`}
            data-testid="filter-unnotified-btn">
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {filterUnnotified ? `Showing ${unnotifiedCount} un-notified` : `Un-notified (${unnotifiedCount})`}
          </Button>
          <Button onClick={() => setShowSchedules(!showSchedules)} variant="outline" size="sm"
            className={`h-9 border-white/10 ${showSchedules ? 'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30' : 'text-white/60 hover:text-white'}`}
            data-testid="toggle-schedules-btn">
            <Calendar className="w-3.5 h-3.5 mr-1.5" /> Scheduled ({schedules.filter(s => s.status === 'pending').length})
          </Button>
          <Button onClick={() => setShowEnquiries(!showEnquiries)} variant="outline" size="sm" className="border-white/10 text-white/60 hover:text-white h-9" data-testid="toggle-enquiries-btn">
            <Users className="w-3.5 h-3.5 mr-1.5" /> Enquiries ({enquiries.length})
          </Button>
          <Button onClick={() => { setEditing('new'); setForm(defaultForm()); }} className="bg-[#D4AF37] text-black hover:bg-[#B8941E] h-9" data-testid="create-deal-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Future Deal
          </Button>
        </div>
      </div>

      {/* Deals Grid */}
      {deals.length === 0 ? (
        <div className="bg-white/5 border border-dashed border-white/10 rounded-lg p-12 text-center">
          <p className="text-white/40 text-sm mb-3">No deals created yet</p>
          <Button onClick={() => { setEditing('new'); setForm(defaultForm()); }} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]">
            <Plus className="w-4 h-4 mr-2" /> Add Your First Deal
          </Button>
        </div>
      ) : visibleDeals.length === 0 ? (
        <div className="bg-[#D4AF37]/5 border border-dashed border-[#D4AF37]/20 rounded-lg p-12 text-center">
          <Send className="w-8 h-8 text-[#D4AF37] mx-auto mb-3 opacity-60" />
          <p className="text-white/70 text-sm mb-1">All live deals have been notified to subscribers</p>
          <p className="text-white/30 text-xs">Toggle the filter off to see all deals.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleDeals.map(deal => (
            <div key={deal.id} className={`bg-white/5 border rounded-lg p-4 group relative transition-all ${deal.visible ? 'border-white/10' : 'border-white/5 opacity-60'}`} data-testid={`admin-deal-${deal.id}`}>
              <div className="flex items-start gap-3">
                {deal.hero_image && (
                  <img src={deal.hero_image} alt="" className="w-14 h-14 rounded-md object-cover border border-white/10 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-medium text-sm leading-tight line-clamp-2">{deal.title}</h4>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {deal.strategy_tags?.slice(0, 3).map((tag, i) => (
                      <span key={i} className="px-1.5 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] rounded">{tag}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-white/40">
                    {deal.finders_fee > 0 && <span>{deal.finders_fee_currency || 'USD'} {deal.finders_fee?.toLocaleString()}</span>}
                    {deal.location && <span>· {deal.location}</span>}
                    {deal.deal_id && <span>· ID: {deal.deal_id}</span>}
                  </div>
                  {deal.last_notified_at && (
                    <div className="mt-1 text-[10px] text-[#D4AF37]/70 flex items-center gap-1">
                      <Send className="w-2.5 h-2.5" /> Notified {deal.last_notified_count || 0} subscribers · {new Date(deal.last_notified_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-0.5">
                  <button onClick={() => notifySubscribers(deal)} className="p-1.5 hover:bg-[#D4AF37]/10 rounded text-white/30 hover:text-[#D4AF37] transition-colors" title="Send now to mailing list" data-testid={`notify-deal-${deal.id}`}><Send className="w-3.5 h-3.5" /></button>
                  <button onClick={() => {
                    if (!deal.visible) { toast.error('Make the deal Live first, then schedule.'); return; }
                    setScheduleModal({ deal });
                  }} className="p-1.5 hover:bg-[#D4AF37]/10 rounded text-white/30 hover:text-[#D4AF37] transition-colors" title="Schedule mailing-list send" data-testid={`schedule-deal-${deal.id}`}><Clock className="w-3.5 h-3.5" /></button>
                  <button onClick={() => copyLink(deal)} className="p-1.5 hover:bg-white/10 rounded text-white/30 hover:text-[#D4AF37] transition-colors" title="Copy link"><Copy className="w-3.5 h-3.5" /></button>
                  <a href={`/deals/${deal.slug}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-white/10 rounded text-white/30 hover:text-white transition-colors" title="Preview"><ExternalLink className="w-3.5 h-3.5" /></a>
                  <a href={`${API_URL}/api/og/deals/${deal.slug}.png?t=${Date.now()}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-[#D4AF37]/10 rounded text-white/30 hover:text-[#D4AF37] transition-colors" title="Preview social-share card (LinkedIn / WhatsApp / X / Telegram)" data-testid={`og-preview-${deal.id}`}><ImageIcon className="w-3.5 h-3.5" /></a>
                  <button onClick={() => startEdit(deal)} className="p-1.5 hover:bg-white/10 rounded text-white/30 hover:text-white transition-colors" title="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => deleteDeal(deal.id)} className="p-1.5 hover:bg-red-500/20 rounded text-white/30 hover:text-red-400 transition-colors" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  {deal.visible ? <Eye className="w-3.5 h-3.5 text-green-400" /> : <EyeOff className="w-3.5 h-3.5 text-white/30" />}
                  <span className={`text-xs ${deal.visible ? 'text-green-400/80' : 'text-white/30'}`}>{deal.visible ? 'Live' : 'Draft'}</span>
                </div>
                <button onClick={() => toggleVisibility(deal)} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${deal.visible ? 'bg-green-500/70' : 'bg-white/15'}`} data-testid={`toggle-deal-${deal.id}`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${deal.visible ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Enquiries Panel */}
      {showEnquiries && enquiries.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <h4 className="text-white font-medium text-sm">Recent Enquiries</h4>
            <span className="text-white/30 text-xs">{enquiries.length} total</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left px-4 py-2 text-white/50 font-medium">Name</th>
                  <th className="text-left px-4 py-2 text-white/50 font-medium">Email</th>
                  <th className="text-left px-4 py-2 text-white/50 font-medium">Phone</th>
                  <th className="text-left px-4 py-2 text-white/50 font-medium">Deal</th>
                  <th className="text-left px-4 py-2 text-white/50 font-medium">Experience</th>
                  <th className="text-left px-4 py-2 text-white/50 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.slice(0, 20).map(enq => (
                  <tr key={enq.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-2.5 text-white">{enq.first_name} {enq.last_name}</td>
                    <td className="px-4 py-2.5 text-white/60">{enq.email}</td>
                    <td className="px-4 py-2.5 text-white/60">{enq.phone}</td>
                    <td className="px-4 py-2.5 text-white/60 max-w-[150px] truncate">{enq.deal_title}</td>
                    <td className="px-4 py-2.5 text-[#D4AF37]">{enq.experience_level}</td>
                    <td className="px-4 py-2.5 text-white/30">{new Date(enq.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Scheduled Notifications Panel */}
      {showSchedules && (
        <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden" data-testid="schedules-panel">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <h4 className="text-white font-medium text-sm">Scheduled Mailing-List Notifications</h4>
            <span className="text-white/30 text-xs">{schedules.length} total</span>
          </div>
          {schedules.length === 0 ? (
            <p className="px-4 py-6 text-white/30 text-xs text-center">No scheduled notifications. Click the clock icon next to any Live deal to schedule one.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left px-4 py-2 text-white/50 font-medium">Deal</th>
                    <th className="text-left px-4 py-2 text-white/50 font-medium">Scheduled For</th>
                    <th className="text-left px-4 py-2 text-white/50 font-medium">Status</th>
                    <th className="text-left px-4 py-2 text-white/50 font-medium">Result</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map(s => (
                    <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-2.5 text-white max-w-[260px] truncate" title={s.deal_title}>{s.deal_title}</td>
                      <td className="px-4 py-2.5 text-white/70">{new Date(s.scheduled_at).toLocaleString()}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                          s.status === 'pending' ? 'bg-[#D4AF37]/15 text-[#D4AF37]' :
                          s.status === 'sent' ? 'bg-green-500/15 text-green-400' :
                          s.status === 'failed' ? 'bg-red-500/15 text-red-400' :
                          'bg-white/10 text-white/40'
                        }`}>{s.status}</span>
                      </td>
                      <td className="px-4 py-2.5 text-white/50 text-[11px]">
                        {s.result ? `${s.result.sent}/${s.result.total}` : (s.error ? <span className="text-red-400" title={s.error}>error</span> : '—')}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {s.status === 'pending' && (
                          <button onClick={() => cancelSchedule(s.id)} className="text-white/40 hover:text-red-400 text-[11px]" data-testid={`cancel-schedule-${s.id}`}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Schedule Modal */}
      {scheduleModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
          onClick={() => setScheduleModal(null)}
          data-testid="schedule-modal">
          <div className="bg-[#0a1a14] border border-[#D4AF37]/30 rounded-xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#D4AF37]" /> Schedule Notification
              </h3>
              <button onClick={() => setScheduleModal(null)} className="p-1 rounded hover:bg-white/5 text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-white/50 text-xs mb-5">
              Email the mailing list about <span className="text-white font-medium">{(scheduleModal.deal.title || '').replace(/\n/g, ' ').slice(0, 60)}</span> at a future time. Uses your local timezone.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div>
                <label className="text-white/50 text-xs block mb-1.5">Date</label>
                <Input type="date" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} className="bg-white/5 border-white/10 text-white text-xs" data-testid="schedule-date" min={new Date().toISOString().slice(0,10)} />
              </div>
              <div>
                <label className="text-white/50 text-xs block mb-1.5">Time</label>
                <Input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} className="bg-white/5 border-white/10 text-white text-xs" data-testid="schedule-time" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button onClick={() => setScheduleModal(null)} variant="ghost" size="sm" className="text-white/50 hover:text-white">Cancel</Button>
              <Button onClick={submitSchedule} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" data-testid="submit-schedule-btn">
                <Calendar className="w-3.5 h-3.5 mr-1.5" /> Schedule Send
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ Reusable Sub-Components ============

const Section = ({ title, action, children }) => (
  <div className="bg-white/[0.03] border border-white/10 rounded-lg p-5">
    <div className="flex items-center justify-between mb-4">
      <h4 className="text-white font-semibold text-sm">{title}</h4>
      {action}
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-white/50 text-xs block mb-1.5">{label}</label>
    {children}
  </div>
);

// ============ Visual Icon Picker Grid ============
// Replaces native <select> dropdown with a popup grid that shows the actual Lucide icons.
// Same API as before: `value` (string) + `onChange(name)`.
// `allowNone=true` enables a "No icon" tile (used for optional per-bullet icons).
// `compact=true` renders a smaller trigger button (used for narrow inline bullet rows).
const IconPickerGrid = ({ value, onChange, allowNone = false, compact = false, testid }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    // Focus search input on open
    setTimeout(() => inputRef.current?.focus(), 30);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const CurrentIcon = value ? (ICON_RENDER_MAP[value] || Info) : null;
  const q = search.trim().toLowerCase();

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-md text-white hover:border-[#D4AF37]/40 transition-colors ${
          compact ? 'px-1.5 py-1 text-[10px] w-20 h-7' : 'px-2 py-1.5 text-xs w-24'
        }`}
        data-testid={testid || 'icon-picker-trigger'}
        title={value || (allowNone ? 'No icon' : 'Pick icon')}
      >
        {CurrentIcon ? (
          <CurrentIcon className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-[#D4AF37] flex-shrink-0`} />
        ) : (
          <span className="text-white/30 text-[10px] flex-shrink-0">none</span>
        )}
        <span className={`truncate flex-1 text-left text-white/60 ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
          {value || ''}
        </span>
        <ChevronDown className="w-3 h-3 text-white/40 flex-shrink-0" />
      </button>

      {open && (
        <div
          className="absolute z-[60] top-full left-0 mt-1 w-[340px] max-h-[420px] overflow-hidden bg-[#0a1a14] border border-[#D4AF37]/30 rounded-lg shadow-2xl flex flex-col"
          data-testid="icon-picker-popup"
        >
          <div className="p-2.5 border-b border-white/10 flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
            <input
              ref={inputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search icons…"
              className="flex-1 bg-transparent text-white text-xs placeholder:text-white/30 focus:outline-none"
              data-testid="icon-picker-search"
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-white/40 hover:text-white/80">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          <div className="overflow-y-auto p-2.5">
            {allowNone && !q && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                className={`flex items-center gap-1.5 w-full text-left px-2 py-1.5 mb-2.5 rounded text-xs border transition-colors ${
                  !value
                    ? 'bg-[#D4AF37]/15 border-[#D4AF37]/40 text-[#D4AF37]'
                    : 'border-white/10 text-white/60 hover:bg-white/5'
                }`}
                data-testid="icon-picker-none"
              >
                <X className="w-3.5 h-3.5" /> No icon (plain bullet)
              </button>
            )}

            {ICON_GROUPS.map(g => {
              const filtered = g.icons.filter(ic => !q || ic.toLowerCase().includes(q));
              if (!filtered.length) return null;
              return (
                <div key={g.label} className="mb-3 last:mb-0">
                  <p className="text-[9px] uppercase tracking-[0.15em] text-[#D4AF37]/70 mb-1.5 px-0.5 font-semibold">
                    {g.label}
                  </p>
                  <div className="grid grid-cols-7 gap-1">
                    {filtered.map(ic => {
                      const Icn = ICON_RENDER_MAP[ic] || Info;
                      const active = value === ic;
                      return (
                        <button
                          key={ic}
                          type="button"
                          onClick={() => { onChange(ic); setOpen(false); setSearch(''); }}
                          title={ic}
                          className={`aspect-square flex items-center justify-center rounded transition-all ${
                            active
                              ? 'bg-[#D4AF37]/20 border border-[#D4AF37] text-[#D4AF37] shadow-sm'
                              : 'border border-white/10 text-white/70 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] hover:border-[#D4AF37]/30'
                          }`}
                          data-testid={`icon-option-${ic}`}
                        >
                          <Icn className="w-4 h-4" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Empty search state */}
            {q && !ICON_GROUPS.some(g => g.icons.some(ic => ic.toLowerCase().includes(q))) && (
              <p className="text-white/40 text-xs text-center py-6">
                No icons match "{search}"
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const IconPicker = (props) => <IconPickerGrid {...props} />;
const BulletIconPicker = (props) => <IconPickerGrid {...props} allowNone compact />;

const CardEditor = ({ card, idx, onUpdate, onRemove }) => {
  // Items can be a string OR { icon, text } object. Helpers below normalise.
  const getText = (item) => typeof item === 'string' ? item : (item?.text || '');
  const getIcon = (item) => typeof item === 'object' && item ? (item.icon || '') : '';
  const updateBulletText = (j, v) => {
    const arr = [...(card.items || [])];
    const cur = arr[j];
    if (typeof cur === 'object' && cur && cur.icon) {
      arr[j] = { icon: cur.icon, text: v };
    } else {
      arr[j] = v;
    }
    onUpdate(idx, 'items', arr);
  };
  const updateBulletIcon = (j, ic) => {
    const arr = [...(card.items || [])];
    const cur = arr[j];
    const text = getText(cur);
    if (!ic) {
      arr[j] = text; // remove icon → revert to plain string
    } else {
      arr[j] = { icon: ic, text };
    }
    onUpdate(idx, 'items', arr);
  };
  const removeBullet = (j) => onUpdate(idx, 'items', (card.items || []).filter((_, k) => k !== j));

  // One-click append → instantly adds an empty row & focuses it. No "type then click Add" friction.
  const appendEmpty = () => {
    const newArr = [...(card.items || []), ''];
    onUpdate(idx, 'items', newArr);
    // Focus the new input on next paint
    setTimeout(() => {
      const el = document.querySelector(`[data-card-idx="${idx}"][data-bullet-idx="${newArr.length - 1}"]`);
      if (el) el.focus();
    }, 50);
  };

  return (
    <div className="bg-[#0d1f17] border border-white/10 rounded-lg p-3 space-y-2" data-testid={`card-editor-${idx}`}>
      <div className="flex items-center gap-2">
        <IconPicker value={card.icon || 'info'} onChange={v => onUpdate(idx, 'icon', v)} />
        <Input value={card.title} onChange={e => onUpdate(idx, 'title', e.target.value)} className="bg-white/5 border-white/10 text-white text-xs flex-1 font-semibold" placeholder="CARD TITLE" />
        <button type="button" onClick={() => onRemove(idx)} className="p-1 hover:bg-red-500/20 rounded text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      <Input value={card.highlight || ''} onChange={e => onUpdate(idx, 'highlight', e.target.value)}
        className="bg-[#D4AF37]/5 border-[#D4AF37]/30 text-[#D4AF37] text-[11px] h-7 placeholder:text-[#D4AF37]/30"
        placeholder='Gold callout (optional). Use "|" for two-cell split. e.g. "20% DOWN PAYMENT | BALANCE OVER 4 YEARS"' />
      <div className="space-y-1.5 pl-1">
        {(card.items || []).map((item, j) => (
          <div key={j} className="flex items-center gap-1.5">
            <BulletIconPicker value={getIcon(item)} onChange={v => updateBulletIcon(j, v)} />
            <Input value={getText(item)} onChange={e => updateBulletText(j, e.target.value)}
              data-card-idx={idx} data-bullet-idx={j}
              className="bg-white/5 border-white/10 text-white text-[11px] flex-1 h-7"
              placeholder="Type bullet text…" />
            <button type="button" onClick={() => removeBullet(j)} className="p-1 text-white/30 hover:text-red-400"><X className="w-3 h-3" /></button>
          </div>
        ))}
        <button type="button" onClick={appendEmpty}
          className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded border-2 border-dashed border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-colors"
          data-testid={`add-bullet-btn-${idx}`}>
          <Plus className="w-3.5 h-3.5" /> Add bullet
        </button>
      </div>
    </div>
  );
};

// (BulletIconPicker is defined above as a thin wrapper around IconPickerGrid with allowNone+compact)
